import { Flex } from "@redotech/arbiter/flex";
import { Icon } from "@redotech/arbiter/icon";
import { RedoProgressBar } from "@redotech/arbiter/progress-bar/redo-progress-bar";
import { Text } from "@redotech/arbiter/text";
import { Card } from "@redotech/redo-customer-portal-app/portal-four/shared-components/card";
import { UseCreationStatusResult } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-creation-status";
import { useTranslation } from "@redotech/redo-i18n";
import FaceFrownIcon from "@redotech/redo-icons/face-frown.svg";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { Status } from "@redotech/redo-return-core-common/returns/temporal-status";
import { memo } from "react";
import { TypeAnimation } from "react-type-animation";
import * as confirmationCss from "../confirmation.module.css";

const EXPECTED_WORKFLOW_DURATION_MS = 30_000; // Increased since we're tracking the full workflow chain
const MAX_TIME_BASED_PROGRESS = 80; // Max progress from time alone (80%)
const ACTIVITY_BOOST = 1.5; // 1.5% per completed activity (reduced since we have fewer visible activities)
const ESTIMATED_TOTAL_ACTIVITIES = 25; // Reduced since we're only showing creation workflow activities
const ERROR_THRESHOLD_MS = 60_000; // Show error screen after 60 seconds

const CreationErrorMessage = memo(function CreationErrorMessage({
  returnType,
  hasCreationStatusError,
}: {
  returnType: ReturnTypeEnum;
  hasCreationStatusError: boolean;
}) {
  const { t } = useTranslation();

  const created =
    returnType === ReturnTypeEnum.CLAIM
      ? t(
          "Your shipping claim was still created. We will send you an email when we are back up so you can continue with your shipping claim process.",
        )
      : returnType === ReturnTypeEnum.WARRANTY
        ? t(
            "Your warranty claim was still created. We will send you an email when we are back up so you can continue with your warranty claim process.",
          )
        : returnType === ReturnTypeEnum.RECYCLE
          ? t(
              "Your recycling request was still created. We will send you an email when we are back up so you can continue with your recycling request process.",
            )
          : t(
              "Your return was still created. We will send you an email when we are back up so you can continue with your return process.",
            );

  const closing = t("We are sorry for the inconvenience.");

  return (
    <>
      {hasCreationStatusError
        ? `${created} ${closing}`
        : `${created} ${t("This page will automatically update when we are back up.")} ${closing}`}
    </>
  );
});

export const CreationStatus = memo(function CreationStatus({
  showCreationStatus,
  creationStatus,
  hasCreationStatusError = false,
  returnType = ReturnTypeEnum.RETURN,
}: {
  showCreationStatus: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  hasCreationStatusError?: boolean;
  returnType?: ReturnTypeEnum;
}) {
  const { t } = useTranslation();

  const timeBetweenAnimations = 4000;

  // Determine error scenario
  const workflowStalled =
    showCreationStatus &&
    creationStatus?.workflowStartTime &&
    Date.now() - new Date(creationStatus.workflowStartTime).getTime() >
      ERROR_THRESHOLD_MS;

  const shouldShowError = hasCreationStatusError || workflowStalled;

  if (!showCreationStatus) {
    return null;
  }

  if (shouldShowError) {
    return (
      <Card fullscreen uiSelector="return-confirmation">
        <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
          <Icon arbiterIconSvg={FaceFrownIcon} color="ghost" size="large" />
          <Text
            fontSize="xl"
            fontWeight="semibold"
            textAlign="center"
            textColor="primary"
          >
            {t("Something is going wrong on our end")}
          </Text>
          <Text
            className={confirmationCss.errorMessage}
            fontSize="md"
            textAlign="center"
            textColor="secondary"
          >
            <CreationErrorMessage
              hasCreationStatusError={hasCreationStatusError}
              returnType={returnType}
            />
          </Text>
        </Flex>
      </Card>
    );
  }

  let percentage = 0;
  if (creationStatus?.activityStatuses) {
    const relevantActivities = creationStatus.activityStatuses || [];
    const completedActivities = relevantActivities.filter(
      (activity) => activity.status === Status.COMPLETED,
    ).length;
    const totalActivitiesStarted = relevantActivities.length;

    let timeProgress = 0;
    let elapsedMs = 0;
    if (creationStatus.workflowStartTime) {
      elapsedMs =
        Date.now() - new Date(creationStatus.workflowStartTime).getTime();
      timeProgress = Math.min(
        (elapsedMs / EXPECTED_WORKFLOW_DURATION_MS) * MAX_TIME_BASED_PROGRESS,
        MAX_TIME_BASED_PROGRESS,
      );
    }
    const estimatedTotal = Math.max(
      ESTIMATED_TOTAL_ACTIVITIES,
      totalActivitiesStarted + 3,
      completedActivities + 5,
    );

    const activityProgress = (completedActivities / estimatedTotal) * 100;
    const weightedProgress = timeProgress * 0.3 + activityProgress * 0.7;
    const activityBoost = completedActivities * ACTIVITY_BOOST;

    const baselineProgress = Math.min(elapsedMs > 0 ? 10 : 0, 10);

    const progress = Math.min(
      baselineProgress + weightedProgress + activityBoost,
      99,
    );

    if (creationStatus.isWorkflowRunning === false) {
      percentage = 100;
    } else {
      percentage = Math.round(Math.max(progress, percentage * 0.95));
    }
  }

  return (
    <Card fullscreen uiSelector="return-confirmation">
      <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
        <TypeAnimation
          className={confirmationCss.typeAnimationText}
          cursor={false}
          omitDeletionAnimation
          sequence={[
            returnType === ReturnTypeEnum.CLAIM
              ? t("Creating shipping claim...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Creating warranty claim...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Creating recycling request...")
                  : t("Creating return..."),
            timeBetweenAnimations,
            returnType === ReturnTypeEnum.CLAIM
              ? t("Generating shipping claim label...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Generating warranty claim label...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Generating recycling request label...")
                  : t("Generating return label..."),
            timeBetweenAnimations,
            t("Updating order..."),
            timeBetweenAnimations,
            t("Finishing up..."),
            timeBetweenAnimations,
            t("This is taking longer than expected..."),
          ]}
          speed={80}
          wrapper="span"
        />

        <Flex
          className={confirmationCss.progressSection}
          dir="column"
          gap="sm"
          w="full"
        >
          <RedoProgressBar percentage={percentage} />
          <Text fontSize="xs" textAlign="center" textColor="secondary">
            {t("{{percentage}}% complete", {
              percentage: Math.round(percentage),
            })}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
});
